// Who delivered a session — the person whose provider numbers go on
// the client's service receipt. Pure, so the default rule is unit-tested.

export interface FundProviderNumber {
  fund: string
  number: string
}

export interface PractitionerSource {
  id: string
  name: string
  type?: string
  is_active?: boolean
  display_order?: number
  provider_number?: string | null
  fund_provider_numbers?: FundProviderNumber[] | null
}

export interface StaffOption {
  id: string
  name: string
  /** A service receipt can only name somebody who holds one. */
  hasProviderNumber: boolean
  /** The funds they hold a number with — what a client's fund is chosen from. */
  funds: string[]
}

/** Mirrors hasProviderNumber in _shared/service-receipt.ts — an association number does not count. */
export function holdsProviderNumber(r: Pick<PractitionerSource, 'provider_number' | 'fund_provider_numbers'>): boolean {
  if (r.provider_number?.trim()) return true
  return (r.fund_provider_numbers ?? []).some((f) => f.fund?.trim() && f.number?.trim())
}

/** Active staff only, in the business's own order. */
export function staffOptions(resources: PractitionerSource[]): StaffOption[] {
  return resources
    .filter((r) => r.type === 'staff' && r.is_active !== false)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || a.name.localeCompare(b.name))
    .map((r) => ({ id: r.id, name: r.name, hasProviderNumber: holdsProviderNumber(r), funds: fundsHeld(r) }))
}

/** The funds someone holds a complete number with, in their own order. */
export function fundsHeld(r: Pick<PractitionerSource, 'fund_provider_numbers'>): string[] {
  return (r.fund_provider_numbers ?? [])
    .filter((f) => f.fund?.trim() && f.number?.trim())
    .map((f) => f.fund.trim())
}

/** Every fund an active staff member holds a number with, once each (ignoring case), A–Z. */
export function businessFunds(resources: PractitionerSource[]): string[] {
  const byKey = new Map<string, string>()
  for (const r of resources) {
    if (r.type !== 'staff' || r.is_active === false) continue
    for (const fund of fundsHeld(r)) if (!byKey.has(fund.toLowerCase())) byKey.set(fund.toLowerCase(), fund)
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b))
}

/**
 * A client-fund picker's options: the funds offered, plus the client's recorded one
 * when it is not among them — else the select cannot show it and saving drops it.
 */
export function fundChoices(funds: string[], recorded: string | null | undefined): string[] {
  const kept = recorded?.trim()
  if (!kept || funds.some((f) => f.toLowerCase() === kept.toLowerCase())) return funds
  return [...funds, kept]
}

/** Matched the way the receipt matches it — ignoring case and spacing. */
export function holdsFundNumber(funds: string[], fund: string | null | undefined): boolean {
  const key = fund?.trim().toLowerCase()
  return Boolean(key) && funds.some((f) => f.toLowerCase() === key)
}

/**
 * Who to pre-select: whoever is recorded, else the one staff member on the booking,
 * else in the service's pool, else in the business. Several at any level is no
 * answer. Mirrors defaultDeliverer in _shared/service-receipt.ts, which /complete uses.
 */
export function defaultPractitioner(
  staff: StaffOption[],
  booking: { recorded?: string | null; resourceIds?: string[] | null; serviceResourceIds?: string[] | null },
): string | null {
  const ids = new Set(staff.map((s) => s.id))
  if (booking.recorded && ids.has(booking.recorded)) return booking.recorded
  for (const pool of [booking.resourceIds, booking.serviceResourceIds]) {
    const people = [...new Set(pool ?? [])].filter((id) => ids.has(id))
    if (people.length === 1) return people[0]
    if (people.length > 1) return null
  }
  return staff.length === 1 ? staff[0].id : null
}

/** The line under the picker — what choosing this person means for the client. */
export function practitionerHint(option: StaffOption | null): string {
  if (!option) return 'Choose who gave the session — the service receipt for a health fund / insurance claim needs their provider number.'
  return option.hasProviderNumber
    ? `A service receipt with ${option.name}'s provider number goes to the client for their health fund / insurance claim once the session is paid.`
    : `${option.name} has no provider number, so no service receipt is sent.`
}
